import React, { useState, useEffect, useRef, useCallback } from 'react';
import { estimatesApi } from '../../services/estimatesApi';

/**
 * Debounce hook
 */
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * LocationInput Component
 * Autocomplete input for location validation via AWS Location Service.
 * Only accepts "City, State" or valid US addresses.
 */
export function LocationInput({
  value,
  onChange,
  onValidationChange,
  placeholder = 'e.g., Denver, CO',
  required = false,
  disabled = false,
  className = '',
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isValidated, setIsValidated] = useState(false);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const debouncedQuery = useDebounce(inputValue, 300);

  // Sync external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
      // If value is set externally (e.g., AI extraction), mark as not validated
      setIsValidated(false);
      if (onValidationChange) onValidationChange(false);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search suggestions when debounced query changes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    // Don't search if value was already validated (user selected a suggestion)
    if (isValidated) return;

    const searchSuggestions = async () => {
      setIsLoading(true);
      try {
        const response = await estimatesApi.searchLocationSuggestions(debouncedQuery);
        setSuggestions(response.suggestions || []);
        if ((response.suggestions || []).length > 0) {
          setIsOpen(true);
        }
      } catch (err) {
        console.error('Location suggestions failed:', err);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    searchSuggestions();
  }, [debouncedQuery, isValidated]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsValidated(false);
    setHighlightedIndex(-1);
    if (onValidationChange) onValidationChange(false);
    onChange(newValue);
  };

  const handleSelectSuggestion = useCallback((suggestion) => {
    const selectedText = suggestion.text;
    setInputValue(selectedText);
    setIsValidated(true);
    setIsOpen(false);
    setSuggestions([]);
    if (onValidationChange) onValidationChange(true);
    onChange(selectedText);
  }, [onChange, onValidationChange]);

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleBlur = () => {
    // Delay to allow click on suggestion
    setTimeout(() => setIsOpen(false), 200);
  };

  const showWarning = inputValue.trim().length > 0 && !isValidated;
  const showValid = isValidated;

  // Base input styles matching the existing inputs in the wizard
  const baseInputClass = 'w-full border rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500';
  const borderClass = showValid
    ? 'border-green-400'
    : showWarning
      ? 'border-yellow-400'
      : 'border-gray-300 dark:border-gray-700';

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={`${baseInputClass} ${borderClass}`}
        />
        {/* Status icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {isLoading ? (
            <svg className="animate-spin h-4 w-4 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : showValid ? (
            <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : showWarning ? (
            <svg className="h-5 w-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ) : null}
        </div>
      </div>

      {/* Validation hint */}
      {showWarning && (
        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
          Select a location from the suggestions to validate
        </p>
      )}

      {/* Suggestions dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.placeId || index}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 ${
                index === highlightedIndex ? 'bg-blue-50 dark:bg-blue-900/30' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectSuggestion(suggestion);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{suggestion.text}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
